import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaShortenerV1Service, serviceName } from '../service';
import { ListBlockedDomainsIO, ServiceQueries } from '../types';
import { useShortenerV1Queries } from './use-shortener-v1-queries';
import { UseShortenerV1QueryEndpointArgs, useShortenerV1Query } from './use-shortener-v1-query';

/**
 * A hook that returns a query for the `ListBlockedDomains` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListBlockedDomainsQuery = <E = unknown, D = ListBlockedDomainsIO['output']>(
  args: UseShortenerV1QueryEndpointArgs<'ListBlockedDomains', E, D>
) =>
  useShortenerV1Query({
    endpointName: 'ListBlockedDomains',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListBlockedDomains` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListBlockedDomainsQueries = <E = unknown, D = ListBlockedDomainsIO['output']>(
  argsArr: UseShortenerV1QueryEndpointArgs<'ListBlockedDomains', E, D>[]
) =>
  useShortenerV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListBlockedDomains',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListBlockedDomains` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListBlockedDomains = () => {
  const queryClient = useQueryClient();

  const fetchListBlockedDomains = async <E = unknown, D = ListBlockedDomainsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseShortenerV1QueryEndpointArgs<'ListBlockedDomains', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListBlockedDomainsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBlockedDomains'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListBlockedDomainsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBlockedDomains'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListBlockedDomains'>({
          serviceName,
          endpointName: 'ListBlockedDomains',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaShortenerV1Service.ListBlockedDomains(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListBlockedDomains;
};
